import { useContext, useEffect, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { ILoginFormValues } from "../../../authentication/_interfaces/ILoginFormValues";
import { IOrganizationSignUpFlowValues } from "../_interfaces/IOrganizationSignUpFlowValues";
import { LoginStepValues } from "../_interfaces/IOrganizationSignUpStepTypeValues";
import { OrganizationSignUpLoginValidation } from "../validation/LoginValidation";
import { RootContext } from "../../../../stores";
import { LoginData } from "../../../../stores/models/LoginData";

export function useLoginController(
    values: LoginStepValues,
    allValues: IOrganizationSignUpFlowValues,
    update: (patch: Partial<LoginStepValues>) => void,
    onSuccess: (override?: Partial<LoginStepValues>) => void
): {
    values: ILoginFormValues,
    onChange: (patch: Partial<ILoginFormValues>) => void,
    onSubmit: (override?: Partial<LoginStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof ILoginFormValues, string>>
} {
    const rootStore = useContext(RootContext);
    const userStore = rootStore.userStore;

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ILoginFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    useEffect(() => {
        update({ backClicked: false, password: "" })
    }, [])

    useEffect(() => {
        if (validationRun) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [values.username, values.password, validationRun])


    const validate = (override?: Partial<LoginStepValues>) => {
        return OrganizationSignUpLoginValidation({ ...values, ...override });
    }

    const isValidSubmission = (override?: Partial<LoginStepValues>) => {
        return validate(override).valid;
    }

    const submit = async (override?: Partial<LoginStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        try {
            const isBackRequest = allValues.backClicked || override?.backClicked;
            const isSignUpRequest = allValues.hasAccount === false || override?.hasAccount === false;
            const shouldSwitchStepsWithoutValidation = isBackRequest || isSignUpRequest;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                const effectiveValues = {
                    ...values,
                    ...override
                }
                const loginData = new LoginData();
                loginData.setUsername(effectiveValues.username);
                loginData.setPassword(effectiveValues.password);
                update({ ...override, loading: true });
                const result = await userStore.login(loginData);
                if (userStore.isAuthenticated) {
                    const updatedOverride = {
                        ...override,
                        loading: false,
                        isAuthenticated: userStore.isAuthenticated,
                        hasExistingOrganizationProfile: userStore.user.hasOrganizationProfile,
                        currentUserName: userStore.user.name,
                        currentUserEmail: userStore.user.email
                    }
                    update(updatedOverride);
                    onSuccess(updatedOverride);
                } else {
                    throw result;
                }
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    default:
                        setError(err.message);
                }
            } else if (typeof err === 'string') {
                setError(err);
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            update({ loading: false });
            setSubmitting(false);
        }
    };

    return {
        values,
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}